// src/pages/Database.js
// Coach-facing Database browser: every topic's teach-ready positions
// (from the `database` collection), grouped, searchable, with board
// previews, notes, solutions, and one-click FEN copy.
import React, { useState, useEffect } from "react";
import { Chessboard } from "react-chessboard";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import "./Database.css";

const GROUP_ORDER = ["Basics", "Checkmates", "Tactics", "Attack", "Defence & Draws", "Endgames", "Openings", "Lessons"];

export default function Database() {
  const [topics, setTopics] = useState(null);   // null = loading
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    getDocs(collection(db, "database"))
      .then(snap => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) =>
          (GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group)) ||
          a.name.localeCompare(b.name));
        setTopics(rows);
      })
      .catch(err => { console.error("Could not load database:", err); setTopics([]); });
  }, []);

  function copyFen(fen, idx) {
    navigator.clipboard?.writeText(fen).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1200);
    }).catch(() => {});
  }

  const filtered = (topics || []).filter(t =>
    !search.trim() || t.name.toLowerCase().includes(search.trim().toLowerCase()));
  const groups = [...new Set(filtered.map(t => t.group))];

  return (
    <div className="db-browser">
      <div className="page-header">
        <div>
          <h2>🗄️ Database</h2>
          <p className="page-sub">Teaching positions for every topic — browse, copy a FEN, or load them live in class</p>
        </div>
      </div>

      {topics === null && <div className="loading-screen" style={{ minHeight: 160 }}><div className="spinner" /></div>}

      {topics !== null && topics.length === 0 && (
        <div className="empty-state"><div>🗄️</div><h3>Database is empty</h3><p>Run <b>build-database.mjs</b> on your computer to fill it.</p></div>
      )}

      {topics !== null && topics.length > 0 && !selected && (
        <>
          <div className="db-search-wrap">
            <input className="db-search" placeholder="🔎 Search topics…" value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="db-search-clear" onClick={() => setSearch("")}>×</button>}
          </div>
          {groups.map(g => (
            <div key={g} className="db-group">
              <h3 className="db-group-title">{g}</h3>
              <div className="db-topic-grid">
                {filtered.filter(t => t.group === g).map(t => (
                  <button key={t.id} className="db-topic-card" onClick={() => setSelected(t)}>
                    <span className="db-topic-name">{t.name}{t.approx ? " ≈" : ""}</span>
                    <span className="db-topic-count">{t.totalPositions} positions</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {selected && (
        <div className="db-positions">
          <div className="db-positions-head">
            <button className="btn btn-secondary" onClick={() => setSelected(null)}>← All topics</button>
            <h3>{selected.name} <span className="db-pos-count">({selected.positions?.length || 0})</span></h3>
          </div>
          <div className="db-pos-grid">
            {(selected.positions || []).map((p, i) => (
              <div key={i} className="db-pos-card">
                <div className="db-pos-board">
                  <Chessboard
                    id={`db-${selected.id}-${i}`}
                    position={p.fen}
                    arePiecesDraggable={false}
                    boardOrientation={p.colorToMove === "b" ? "black" : "white"}
                    customDarkSquareStyle={{ backgroundColor: "#7b5c3e" }}
                    customLightSquareStyle={{ backgroundColor: "#e9dcc0" }}
                  />
                </div>
                <div className="db-pos-meta">
                  <span className="db-pos-turn">{p.colorToMove === "w" ? "⚪ White" : "⚫ Black"} to move</span>
                  {p.note && <p className="db-pos-note">{p.note}</p>}
                  {p.solution && p.solution.length > 0 && (
                    <details className="db-pos-solution">
                      <summary>Solution</summary>
                      <span>{p.solution.join(" ")}</span>
                    </details>
                  )}
                  <button className="btn btn-secondary db-copy-btn" onClick={() => copyFen(p.fen, i)}>
                    {copiedIdx === i ? "✓ Copied!" : "Copy FEN"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
